import React from 'react'
import {strftime} from '@fotolog/xutil'
import {useController, useSuspense} from '@data-client/react'
import Select, {MenuPlacement, SingleValue} from 'react-select'
import {AppMsg, Collapse, Field, PanelProps, SelectTags, SubmitButton} from '.'
import {PublicTripResource, Trip, TripResource, exportTrip, TripsFilter} from '../lib/model'
import {
  apiReply,
  FormFieldProps,
  galleryCaption,
  getDataAttr,
  scrollTo,
  toast,
  useAppNav,
  useForm,
  useSession,
  useToggle,
  useToggleReturn,
  val2opt,
} from '../lib'
import {Controller as FormController, UseFormReturn} from 'react-hook-form'
import {AddTripForm, UpdateTripForm, FilterTripsForm} from '../lib/form/Trip'
import {AddTripSchema, UpdateTripSchema, FilterTripsSchema} from '../lib/schema/Trip'
import {FotoCard, FotoUploader, SelectFoto} from './Fotos'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardMedia,
  FormHelperText,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import {
  IconUploadFotos,
  IconExport,
  IconDelete,
  IconImport,
  IconUpdate,
  IconHide,
  IconAccess,
  IconLocate,
  IconTripMap,
  IconMenu,
  IconRoute,
  IconShare,
  IconTripReport,
  IconNavPrev,
  IconNavNext,
  IconClose,
} from '../Icons'
import {useLocation} from 'wouter'
import {TripMap, useMapState} from './Map'
import LightGallery from 'lightgallery/react'
import lgZoom from 'lightgallery/plugins/zoom'
import lgThumbnail from 'lightgallery/plugins/thumbnail'
import lgAuto from 'lightgallery/plugins/autoplay'
import lgFull from 'lightgallery/plugins/fullscreen'
import {micromark} from 'micromark'
import {useWindowSize} from 'react-use'
import axios from 'axios'
import {IMapable} from '@fotolog/data'

type TripNavOptionProps = {
  trip: Trip
  size?: number
  truncate?: boolean
}
function TripNavOption({trip, size = 100, truncate = true}: TripNavOptionProps) {
  return (
    <Box display="flex" alignItems="center">
      <Box
        mr={1}
        className="app-profile-img app-hide-sm"
        sx={{
          width: size,
          height: size,
          borderRadius: '10%',
          backgroundImage: `url(${trip.cover?.thumbnail})`,
        }}
      />
      <Box width="100%" className="app-flex" flexWrap="wrap">
        <Typography className="app-flex" variant="h5" sx={{whiteSpace: 'normal'}}>
          {trip.title}
          {trip.user?.name && (
            <Avatar
              alt={trip.user.name}
              title={trip.user.name}
              src={trip.user.avatar}
              sx={{ml: 2, width: 50, height: 50, boxShadow: '4px 2px 6px #999'}}
            />
          )}
        </Typography>
        <Box textAlign="right" ml="auto">
          {trip.fotos.length} Fotos
          <br />
          {strftime(trip.happened, 'PPPP')}
        </Box>
      </Box>
    </Box>
  )
}

type TripOption = {
  value: Trip
  label: React.ReactElement
}

type TripNavProps = {
  isPublic?: boolean
  defval?: number
}

export function TripNav({isPublic = false, defval}: TripNavProps) {
  const setLocation = useLocation()[1]
  const session = useSession()
  // TODO: use defaultTripFilters()
  const trips = useSuspense((isPublic ? PublicTripResource : TripResource).getList, {scope: session.euid(), tags: []})
  const [trip, setTrip] = React.useState<Trip>()
  const size = 100
  const option = (trip: Trip) => ({
    value: trip,
    label: <TripNavOption trip={trip} size={size} />,
  })
  React.useEffect(() => {
    if (trips.length === 1) {
      setTrip(trips[0])
    } else if (defval) {
      const item = trips.find(i => i.id === defval)
      if (item) {
        setTrip(item)
      }
    }
  }, [defval, trips.map(i => i.id).join(',')]) // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Select
      onChange={(o: SingleValue<TripOption>) => {
        const trip = o?.value
        if (trip) {
          setTrip(trip)
          setLocation(`/trip/${trip.id}-${trip.slug}`)
        }
      }}
      value={trip ? option(trip) : null}
      isMulti={false}
      isSearchable={true}
      getOptionValue={(o: TripOption) => o.value.title}
      options={trips.map(option)}
      placeholder={'Select a Trip or type to search...'}
      classNamePrefix="react-select"
      styles={{
        valueContainer: provided => ({
          ...provided,
          height: size + 20,
        }),
      }}
    />
  )
}

function defaultTripFilters(scope: number): TripsFilter {
  const stored = localStorage.getItem('trips.filter')
  if (stored) {
    try {
      const d = JSON.parse(stored) as TripsFilter
      if (d.scope === scope) {
        // only if saved filter matches current scope
        return d
      }
    } catch {
      console.log('resetting unsupported trips.filter')
      localStorage.removeItem('trips.filter')
    }
  }
  return {scope, tags: []}
}

interface TripFilterFormProps {
  show: useToggleReturn
  onSearch: (data: TripsFilter) => void
}

export function TripFilterForm({show, onSearch}: TripFilterFormProps) {
  const session = useSession()
  // const filters = useToggle(false)
  const form = useForm(FilterTripsSchema)
  React.useEffect(() => {
    const fields = defaultTripFilters(session.euid())
    form.setValue('start', fields.start ? new Date(fields.start) : null)
    form.setValue('end', fields.end ? new Date(fields.end) : null)
    if (fields.tags) {
      form.setValue('tags', fields.tags.map(val2opt))
    }
    onSearch(fields)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
  /* @ts-expect-error */
  const onSubmit = form.handleSubmit(async (data: FilterTripsSchema) => {
    const search: TripsFilter = {
      scope: session.euid(),
      tags: [],
    }
    if (data.tags) {
      search.tags = data.tags.map(i => i.value)
    }
    if (data.start) {
      search.start = Number(strftime(data.start, 'T', {utc: true}))
    }
    if (data.end) {
      search.end = Number(strftime(data.end, 'T', {utc: true}))
    }
    onSearch(search)
    localStorage.setItem('trips.filter', JSON.stringify(search))
  })

  const onClearFilters = () => {
    localStorage.removeItem('trips.filter')
    form.setValue('start', null)
    form.setValue('end', null)
    form.setValue('tags', [])
    onSearch({scope: session.euid(), tags: []})
  }

  if (!show.enabled) {
    return null
  }

  return (
    <Paper elevation={3} sx={{p: 2, mt: 1}}>
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Typography variant="h3">Filters</Typography>
        <IconClose onClick={show.off} className="app-clickable" color="primary" fontSize="large" />
      </Box>
      <form onSubmit={onSubmit}>
        <Grid container columns={4} spacing={4}>
          <Grid size={{xs: 4, sm: 2, md: 1}}>
            <Field form={form} attrs={FilterTripsForm.start} />
          </Grid>
          <Grid size={{xs: 4, sm: 2, md: 1}}>
            <Field form={form} attrs={FilterTripsForm.end} />
          </Grid>
          <Grid size={{xs: 4, md: 2}}>
            <SelectTags form={form} attrs={FilterTripsForm.tags} tags={session.scope.tags} />
          </Grid>
        </Grid>
        <Box display="flex" justifyContent="center" flexDirection={{xs: 'column', sm: 'row'}} mt={3}>
          <Button color="error" onClick={onClearFilters}>
            <IconDelete /> &nbsp; Clear Filters
          </Button>
          <div style={{width: 40, height: 20}} />
          <SubmitButton form={form}>Apply Filters</SubmitButton>
        </Box>
      </form>
    </Paper>
  )
}

interface TripSummaryProps {
  trip: Trip
  size?: number
  truncate?: boolean
}
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function TripSummary({trip, size = 100, truncate = true}: TripSummaryProps) {
  return (
    <Box display="flex" alignItems="center">
      <Box
        mr={1}
        className="app-profile-img"
        sx={{
          width: size,
          height: size,
          borderRadius: '10%',
          backgroundImage: `url(${trip.cover?.thumbnail})`,
        }}
      />
      <div className={truncate ? 'app-truncate' : ''} title={trip.title} style={{width: '100%'}}>
        <div>{trip.title}</div>
        <Box className="app-flex" color="text.secondary" fontSize="small">
          <div>{trip.fotos.length} Fotos</div>
          <div>{strftime(trip.happened, 'PPP')}</div>
        </Box>
      </div>
    </Box>
  )
}

type SelectTripProps = {
  form: UseFormReturn
  name: string
  trips: Trip[]
  defvals?: number[]
  attrs: FormFieldProps
  drop?: 'up' | 'down' | 'auto'
}
export function SelectTrip({form, name, trips, defvals, attrs, drop = 'down'}: SelectTripProps) {
  const option = (trip: Trip) => ({
    value: trip.id,
    label: trip.title,
  })
  React.useEffect(() => {
    if (defvals) {
      const newvals = trips.filter(i => defvals.includes(i.id))
      if (newvals.length) {
        form.setValue(name, attrs.multi ? newvals.map(option) : option(newvals[0]), {shouldValidate: true})
      }
    }
  }, [defvals?.join(':'), trips.map(i => i.id).join(',')]) // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <FormHelperText component="div" variant="filled" sx={{fontSize: '1rem'}}>
        {attrs.label}
      </FormHelperText>
      <FormController
        control={form.control}
        name={name}
        render={({field}) => (
          <Select
            {...field}
            isMulti={attrs.multi}
            isSearchable={attrs.searchable}
            getOptionValue={o => o.label}
            options={trips.map(option)}
            placeholder={attrs.placeholder || 'Select a Trip...'}
            menuPlacement={{auto: 'auto', down: 'bottom', up: 'top'}[drop] as MenuPlacement}
            classNamePrefix="react-select"
            className={form.formState.errors[name] ? 'is-invalid' : ''}
          />
        )}
      />
      <FormHelperText component="div" variant="filled" sx={{fontSize: '1rem'}}>
        {attrs.help}
      </FormHelperText>
    </>
  )
}

type TripCardProps = {
  trip: Trip
  isPublic: boolean
}
export function TripCard({trip, isPublic}: TripCardProps) {
  const showReport = useToggle()
  const nav = useAppNav()
  const {width} = useWindowSize()
  const map = useMapState()
  const foto = trip.cover
  const mapable =
    foto?.gps &&
    ({
      foto,
      trip: trip,
      id: trip.id,
      title: trip.title,
      img: foto.thumbnail,
      gps: foto.gps,
      marker: false,
      clicked: 0,
    } as IMapable)
  return (
    <Card>
      <CardHeader
        action={
          mapable && (
            <IconLocate
              onClick={() => {
                map.openMarker(mapable)
                if (width < 500) {
                  nav.back()
                }
              }}
              className="app-clickable"
              color={map.marker?.id === trip.id ? 'secondary' : 'primary'}
              fontSize="large"
            />
          )
        }
        title={trip.title}
      />
      <CardMedia
        component="img"
        width="100%"
        image={trip.cover?.thumbnail}
        alt={trip.title || `Trip ${trip.id}`}
        className="app-framed"
      />
      <CardActions disableSpacing>
        <IconButton
          href={`/trip/${trip.id}-${trip.slug}`}
          color="primary"
          aria-label="view trip"
          title="View Trip on Map"
        >
          <IconTripMap />
        </IconButton>
        <IconButton
          color="primary"
          aria-label="share"
          title="Share Trip"
          onClick={() => {
            navigator.clipboard.writeText(`https://fotolog.tfks.net/trip/${trip.id}-${trip.slug}/`)
            toast('info', 'Link Copied to Clipboard')
          }}
        >
          <IconShare />
        </IconButton>
        {trip.report && (
          <IconButton color="primary" aria-label="trip report" title="Toggle Report" onClick={showReport.toggle}>
            <IconTripReport />
          </IconButton>
        )}
        <Box textAlign="right" fontSize={14} color="text.secondary" flexGrow={1}>
          {trip.fotos.length} Fotos on <br />
          {strftime(trip.happened, 'PPP')}
        </Box>
      </CardActions>
      {showReport.enabled && (
        <CardContent>
          <Typography dangerouslySetInnerHTML={{__html: micromark(trip.report)}} />
        </CardContent>
      )}
    </Card>
  )
}

export function AddTrip({session, params}: PanelProps) {
  const nav = useAppNav()
  const api = useController()
  const form = useForm(AddTripSchema, {defaults: {happened: null, tags: []}})

  /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
  /* @ts-expect-error */
  const onSubmit = form.handleSubmit(async (data: AddTripSchema) => {
    const msg = toast('loading', 'Adding Trip')
    let tags: string[]
    if (data.tags) {
      tags = data.tags.map(i => i.value)
    }
    const reply = await apiReply(() => api.fetch(TripResource.getList.push, {...data, tags, scope: session.euid()}))
    if (reply.error) {
      msg.fail(reply.error)
      return
    }
    msg.succeed()
    // TODO? api.invalidate(TripResource.getList, {})
    nav.to(`/trip/${reply.id}-${reply.slug}/`)
  })

  return (
    <form onSubmit={onSubmit}>
      <Typography variant="body1" p={1}>
        Provide some basic info about your trip. Once added, you will be able to upload Fotos. You may also import a
        Trip that you previously exported.
      </Typography>
      <Box textAlign="center">
        <Button href="/globe/import" color="primary" variant="outlined">
          <IconImport /> &nbsp; Import
        </Button>
      </Box>
      <Box my={5}>
        <Field form={form} attrs={AddTripForm.title} />
      </Box>
      <Box my={5}>
        <Field form={form} attrs={AddTripForm.happened} />
      </Box>
      <Box my={5}>
        <Field form={form} attrs={AddTripForm.report} />
      </Box>
      <Box my={5}>
        <SelectTags form={form} attrs={AddTripForm.tags} tags={session.scope.tags} />
      </Box>
      <Box mt={3} textAlign="center">
        <SubmitButton form={form}>Add Trip</SubmitButton>
      </Box>
    </form>
  )
}

export function UpdateTrip({session, params}: PanelProps) {
  const nav = useAppNav()
  const api = useController()
  const trip = useSuspense(TripResource.get, {id: params['id']})
  const trips = useSuspense(TripResource.getList, {scope: session.euid(), tags: []}) // TODO: sticky tags
  const form = useForm(UpdateTripSchema, {defaults: {...trip, tags: trip.tags?.map(val2opt)}})

  /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
  /* @ts-expect-error */
  const onSubmit = form.handleSubmit(async (data: UpdateTripSchema) => {
    const msg = toast('loading', 'Updating Trip')
    let coverId: number
    if (data.coverId) {
      coverId = data.coverId.value
    }
    let priorId: number
    if (data.priorId) {
      priorId = data.priorId.value
    }
    let parentId: number
    if (data.parentId) {
      parentId = data.parentId.value
    }
    let tags: string[]
    if (data.tags) {
      tags = data.tags.map(i => i.value)
    }
    const reply = await apiReply(() =>
      api.fetch(TripResource.update, {id: trip.id}, {...data, tags, coverId, priorId, parentId})
    )
    if (reply.error) {
      msg.fail(reply.error)
      return
    }
    msg.succeed()
    api.invalidate(TripResource.get, {id: trip.id})
    nav.back()
  })

  return (
    <form onSubmit={onSubmit}>
      <Box my={5}>
        <Field form={form} attrs={UpdateTripForm.title} />
      </Box>
      <Box my={5}>
        <Field form={form} attrs={UpdateTripForm.report} />
      </Box>
      <Box my={5}>
        <Field form={form} attrs={UpdateTripForm.happened} />
      </Box>
      <Box my={5}>
        <SelectFoto form={form} fotos={trip.fotos} defval={trip.cover?.id} attrs={UpdateTripForm.cover} />
      </Box>
      <Box my={5}>
        <SelectTags form={form} attrs={UpdateTripForm.tags} tags={session.scope.tags} />
      </Box>
      <Box my={5}>
        <SelectTrip
          form={form}
          attrs={UpdateTripForm.prior}
          name="priorId"
          defvals={[trip.priorId || 0]}
          trips={trips}
        />
      </Box>
      <Box my={5}>
        <SelectTrip
          form={form}
          attrs={UpdateTripForm.parent}
          name="parentId"
          defvals={[trip.parentId || 0]}
          trips={trips}
        />
      </Box>
      <Box mt={3} textAlign="center">
        <SubmitButton form={form}>Save Changes</SubmitButton>
      </Box>
    </form>
  )
}

export function DeleteTrip({session, params}: PanelProps) {
  const nav = useAppNav()
  const api = useController()
  const trip = useSuspense(TripResource.get, {id: params['id']})

  const onSubmit = async () => {
    const msg = toast('loading', 'Deleting Trip')
    const reply = await apiReply(() => api.fetch(TripResource.delete, {id: params['id']}))
    if (reply.error) {
      msg.fail(reply.error)
      return
    }
    msg.succeed()
    nav.to('/trips')
  }

  return (
    <>
      <Typography variant="h3" my={3}>
        {trip.title}
      </Typography>
      <AppMsg severity="error" size="medium">
        This action can't be undone.
        <br />A Trip can be exported for
        <br />
        future import if desired.
        <br />
        <br />
        Are you sure?
      </AppMsg>
      <Box mt={3} display="flex" justifyContent="center">
        <Button color="secondary" sx={{mr: 2}} onClick={() => nav.back()}>
          Cancel
        </Button>
        <Button color="primary" onClick={onSubmit}>
          Yes, Delete
        </Button>
      </Box>
    </>
  )
}

export function ExportTrip({session, params}: PanelProps) {
  const nav = useAppNav()
  const api = useController()
  const trip = useSuspense(TripResource.get, {id: params['id']})

  const onSubmit = async () => {
    const msg = toast('loading', 'Exporting Trip')
    const reply = await apiReply(() => api.fetch(exportTrip, {id: Number(params['id'])}, {}))
    if (reply.error) {
      msg.fail(reply.error)
      return
    }
    msg.succeed('Task Queued')
    nav.back()
  }

  return (
    <>
      <Typography variant="h3" my={3}>
        {trip.title}
      </Typography>
      <Paper sx={{p: 2, my: 2, mx: 1}}>
        Since exporting a Trip can take a while, the process will happen in the background.
        <br />
        <br />
        Once the export is ready, an email notification will be sent with a download link.
      </Paper>
      <Box mt={3} textAlign="center">
        <Button color="primary" onClick={onSubmit} variant="contained">
          <IconExport /> &nbsp; Export Trip
        </Button>
      </Box>
    </>
  )
}

export function ShareTrip({session, params}: PanelProps) {
  const nav = useAppNav()
  const api = useController()
  const id = Number(params['id'])
  const trip = useSuspense(TripResource.get, {id})

  const onSubmit = async () => {
    const msg = toast('loading', trip.public ? 'Hiding Trip from Public' : 'Making Trip Publically Available')
    const reply = await apiReply(() => api.fetch(TripResource.update, {id}, {public: !trip.public}))
    if (reply.error) {
      msg.fail(reply.error)
      return
    }
    msg.succeed()
    nav.back()
  }

  return (
    <>
      <Typography variant="h3" my={3}>
        {trip.title}
      </Typography>
      <Paper sx={{p: 2, my: 2, mx: 1}}>
        {trip.public
          ? 'This Trip is currently visible to the public.  You can undo that here.'
          : 'There are a number of options to share this Trip.  The simplest is to make it publically available so that no one needs an account or access token.'}
        <br />
        <br />
        If you prefer to share with specific people, then defining an Access Rule is the better option.
      </Paper>
      <Box mt={3} textAlign="center">
        <Button color="secondary" href="/acl" sx={{mr: 2}}>
          <IconAccess /> &nbsp; Access Rules
        </Button>
        <Button color="primary" onClick={onSubmit} variant="contained">
          {trip.public ? <IconHide /> : <IconShare />} &nbsp; {trip.public ? 'Make Private' : 'Make Public'}
        </Button>
      </Box>
    </>
  )
}

export function ImportTrip({session, params}: PanelProps) {
  const nav = useAppNav()

  const onSelected = async (evt: React.ChangeEvent<HTMLInputElement>) => {
    const msg = toast('loading', 'Importing Trip')
    if (!evt.target.files?.length) {
      return
    }
    const file = evt.target.files[0]
    const form = new FormData()
    if (file.type !== 'application/zip') {
      msg.fail(`${file.type} does not appear to be a Trip export`)
      return
    }
    form.append('zip', file)
    form.append('userId', String(session.euid()))
    await axios.post('/api/trip/import', form, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'App-User': `${session.id}`, // helps with trouble-shooting
      },
      onUploadProgress: evt => {
        if (evt.progress) {
          msg.progress(Math.floor(100 * evt.progress))
        }
      },
    })
    msg.succeed('Import accepted. An email will be sent once complete.')
    nav.back()
  }

  return (
    <>
      <Typography variant="body1" p={1}>
        Select an exported Trip file (.zip) from your device to begin. <br />
        <br />
        If there are many Fotos, then the process may take a while. On average, it takes a few seconds to process each
        Foto.
      </Typography>
      <Box textAlign="center" mt={3}>
        <Button component="label" color="primary" variant="contained">
          <IconImport /> &nbsp; Select File
          <input id="import-file" type="file" accept=".zip" capture="environment" onChange={onSelected} hidden />
        </Button>
      </Box>
    </>
  )
}

// working around: https://github.com/sachinchoolur/lightGallery/issues/1416
const LightBox = React.memo(({tripId, children}: {tripId: number; children: React.ReactNode}) => {
  return (
    <LightGallery
      plugins={[lgZoom, lgThumbnail, lgAuto, lgFull]}
      licenseKey="DACB6A32-2FC34AA4-BDA828A5-9987BEDC"
      mode="lg-fade"
      selector={`.lg-trip-${tripId}`}
      appendSubHtmlTo={'.lg-item'}
      thumbnail={true}
      toggleThumb={true}
      slideShowAutoplay={false}
      autoplayControls={true}
      progressBar={true}
      fullScreen={true}
      zoom={true}
      zoomFromOrigin={true}
      mobileSettings={{controls: true, showCloseIcon: true, download: true, autoplay: true, fullScreen: true}}
      exThumbImage="data-thumb"
      allowMediaOverlap={true}
      onInit={({instance}) => {
        window.app.lightbox = instance
      }}
    >
      {children}
    </LightGallery>
  )
})

type TripViewProps = {
  trip: Trip
  children?: React.ReactNode
}

export function TripView({trip, children}: TripViewProps) {
  const [location] = useLocation()
  const {width} = useWindowSize()
  const detailing = location.match(`/${trip.id}-[-\\w]+/`)
  const right = detailing ? 400 : 0 // TODO: use PANEL_WIDTH constant

  return (
    <LightBox tripId={trip.id}>
      {trip.fotos.map(foto => (
        <img
          key={`foto-${foto.id}`}
          className={`lg-trip-${trip.id}`}
          data-src={foto.url}
          data-thumb={foto.thumbnail}
          data-download={foto.caption.replace(/\W+/gu, '-') + '.jpg'}
          data-download-url={foto.urls?.orig || foto.url}
          data-sub-html={galleryCaption(foto.caption, foto.story)}
          src={`${foto.url}`}
          title={foto.caption}
          alt={foto.caption}
          loading="lazy"
          style={{display: 'none'}}
        />
      ))}
      {trip.children.map(child => {
        const foto = child.cover
        if (!foto) {
          return null
        }
        return (
          <img
            key={`foto-${foto.id}`}
            className={`lg-trip-${trip.id}`}
            data-src={foto.url}
            data-thumb={foto.thumbnail}
            data-download={foto.caption.replace(/\W+/gu, '-') + '.jpg'}
            data-download-url={foto.urls?.orig || foto.url}
            data-sub-html={galleryCaption(foto.caption, foto.story)}
            src={`${foto.url}`}
            title={child.title}
            alt={child.title}
            loading="lazy"
            style={{display: 'none'}}
          />
        )
      })}
      <Box position="fixed" top={80} left={0} bottom={0} right={right}>
        <TripMap trip={trip} width={width - right} />
        {detailing ? null : children}
      </Box>
    </LightBox>
  )
}

type TripDetailsProps = {
  trip: Trip
  isPublic?: boolean
  showCover?: boolean
}
function TripDetails({trip, isPublic = false, showCover = false}: TripDetailsProps) {
  return (
    <>
      <Box className="header" mb={3}>
        {isPublic ? (
          <Typography variant="h3" py={2} px={1} mr="20px" color="secondary">
            {trip.title}
          </Typography>
        ) : (
          <Collapse>
            <Box display="flex" alignItems="center">
              <IconButton color="primary" title="Trip Actions">
                <IconMenu />
              </IconButton>
              <Typography variant="h3" py={2} color="primary">
                {trip.title}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-around" width="100%">
              <IconButton href={`../update`} color="primary" title="Update Trip">
                <IconUpdate />
              </IconButton>
              <RouteUploader trip={trip}>
                <IconRoute />
              </RouteUploader>
              <FotoUploader trip={trip}>
                <IconUploadFotos />
              </FotoUploader>
              {trip.fotos.length + trip.children.length > 0 && (
                <>
                  <IconButton href={`../export`} color="primary" title="Export Trip">
                    <IconExport />
                  </IconButton>
                  <IconButton href={`../share`} color="primary" title="Share Trip">
                    <IconShare />
                  </IconButton>
                </>
              )}
              <IconButton href={`../delete`} color="primary" title="Delete Trip">
                <IconDelete />
              </IconButton>
            </Box>
          </Collapse>
        )}
      </Box>
      {showCover && trip.cover && <img src={trip.cover.url} alt="cover foto" width="100%" className="app-framed" />}
      <Typography component="div" variant="body2" display="flex" justifyContent="space-evenly" my={3}>
        {trip.priorId && (
          <IconButton href={`../../${trip.priorId}/`} title="Prior Trip" color="primary">
            <IconNavPrev fontSize="large" />
          </IconButton>
        )}
        <Box textAlign="center">
          <div>{trip.fotos.length + trip.children.length} Fotos & Sub-Trips</div>
          <div>{strftime(trip.happened, 'PPPP')}</div>
        </Box>
        {trip.nextId && (
          <IconButton href={`../../${trip.nextId}/`} title="Next Trip" color="primary">
            <IconNavNext fontSize="large" />
          </IconButton>
        )}
      </Typography>
      {trip.report && <Paper sx={{px: 2, py: 1, my: 2}} dangerouslySetInnerHTML={{__html: micromark(trip.report)}} />}
    </>
  )
}

export function TripSummaryPanel({params}: PanelProps) {
  // TODO: tags, acls, public
  const trip = useSuspense(TripResource.get, {id: params['id']})
  return (
    <>
      <TripDetails trip={trip} isPublic={false} showCover={true} />
      <Box mt={5} textAlign="center">
        <Button variant="contained" href={`/trip/${trip.id}-${trip.slug}/`} color="primary" title="Trip Map">
          <IconTripMap /> &nbsp; Show Fotos on Map
        </Button>
      </Box>
    </>
  )
}

type TripFotosProps = {
  trip: Trip
  fotoId: number
  isPublic: boolean
}
function TripFotos({trip, fotoId, isPublic}: TripFotosProps) {
  React.useEffect(() => {
    if (fotoId) {
      // need to give some time for DOM to update
      setTimeout(() => scrollTo(`foto-${fotoId}`), 200)
    }
    // one way to do something on panel close
    // let foo = false
    // return () => {
    //   if (foo) {
    //   nav(`/trip/${trip.id}`)
    //   }
    //   foo = true
    // }
  }, [fotoId])
  const cards = trip.cards()
  return (
    <>
      <TripDetails trip={trip} isPublic={isPublic} />
      {cards.map(f => (
        <Box id={`foto-${f.id}`} key={`foto-${f.id}`} mt={3} sx={{scrollMarginTop: 90}}>
          <FotoCard trip={trip} foto={f} isPublic={isPublic} isSubTrip={f.isCover} />
        </Box>
      ))}
      {cards.length === 0 && (
        <Box mt={3}>
          <Alert severity="error" icon={false}>
            <Typography variant="h3" color="error">
              No Fotos Uploaded Yet
            </Typography>
            <Typography variant="body1" sx={{my: 2, fontWeight: 700}}>
              IMPORTANT NOTE:
            </Typography>
            <Typography variant="body1">
              When uploading from a mobile device, make sure to select by browsing from the device image library instead
              of the list of images presented initially. Otherwise, the geolocation information will not be included.
            </Typography>
            <Box textAlign="center" mt={2}>
              <FotoUploader trip={trip}>
                <IconUploadFotos /> &nbsp; Upload Fotos
              </FotoUploader>
            </Box>
          </Alert>
        </Box>
      )}
    </>
  )
}

export function PrivateTripFotos({params}: PanelProps) {
  const trip = useSuspense(TripResource.get, {id: params['trip']})
  return <TripFotos trip={trip} fotoId={Number(params['foto'])} isPublic={false} />
}
PrivateTripFotos.CLOSE_BEHAVIOR = 'home'

export function PublicTripFotos({params}: PanelProps) {
  const token = localStorage.getItem('trip.token')
  const trip = useSuspense(PublicTripResource.get, {id: params['trip'], token})
  return <TripFotos trip={trip} fotoId={Number(params['foto'])} isPublic={true} />
}
PublicTripFotos.CLOSE_BEHAVIOR = 'home'

type TripListProps = {
  trips: Trip[]
  tripId: number
  isPublic: boolean
}
function TripList({trips, tripId, isPublic}: TripListProps) {
  React.useEffect(() => {
    if (tripId) {
      // need to give some time for DOM to update
      setTimeout(() => scrollTo(`trip-${tripId}`), 500)
    }
  }, [tripId])
  return (
    <>
      <Typography variant="body1" my={3}>
        Trips that match your search filters and that are also visible on the map are listed below. Click on the map
        marker <IconLocate color="primary" /> to locate the Trip on the map.
      </Typography>
      {trips.map(t => (
        <Box id={`trip-${t.id}`} key={`trip-${t.id}`} mt={3} sx={{scrollMarginTop: 90}}>
          <TripCard trip={t} isPublic={isPublic} />
        </Box>
      ))}
      {trips.length === 0 && <AppMsg severity="warning">No Fotos Uploaded Yet</AppMsg>}
    </>
  )
}

export function PublicGlobeTrips({trips, params}: PanelProps) {
  if (!trips) {
    return null
  }
  return <TripList trips={trips} tripId={Number(params['trip'])} isPublic={true} />
}
PublicGlobeTrips.CLOSE_BEHAVIOR = 'home'

export function PrivateGlobeTrips({trips, params}: PanelProps) {
  if (!trips) {
    return null
  }
  return <TripList trips={trips} tripId={Number(params['trip'])} isPublic={false} />
}
PrivateGlobeTrips.CLOSE_BEHAVIOR = 'home'

type TripRowProps = {
  trip: Trip
}
export function TripRow({trip}: TripRowProps) {
  return (
    <TableRow
      data-id={trip.id}
      data-slug={trip.slug}
      hover
      className="app-clickable"
      sx={{'&:last-child td, &:last-child th': {border: 0}}}
    >
      <TableCell align="center">{trip.fotos.length}</TableCell>
      <TableCell>
        <Typography variant="h6" className="app-truncate">
          {trip.title}
        </Typography>
      </TableCell>
      <TableCell align="right" className="app-hide-sm">
        {strftime(trip.happened, 'PPP')}
      </TableCell>
    </TableRow>
  )
}

// TODO: use a grid:
// - https://mui.com/x/react-data-grid/

type TripTableProps = {
  trips: Trip[]
}
export function TripTable({trips}: TripTableProps) {
  const nav = useAppNav()
  const onDetails = (evt: React.SyntheticEvent<HTMLElement>) => {
    const id = getDataAttr('id', evt.target as HTMLElement)
    const slug = getDataAttr('slug', evt.target as HTMLElement)
    nav.to(`${id}-${slug}/`)
  }
  if (trips.length === 0) {
    return <AppMsg severity="warning">No Trips Have Been Added</AppMsg>
  }
  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell align="center">Fotos</TableCell>
            <TableCell align="left">Description</TableCell>
            <TableCell align="right" className="app-hide-sm">
              Happened
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody onClick={onDetails}>
          {trips.map(i => (
            <TripRow key={i.id} trip={i} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

type RouteUploaderProps = {
  trip: Trip
  children: React.ReactNode
}
export function RouteUploader({trip, children}: RouteUploaderProps) {
  const session = useSession()
  const api = useController()

  const onSelected = async (evt: React.ChangeEvent<HTMLInputElement>) => {
    const msg = toast('loading', 'Uploading Route')
    if (evt.target.files == null) {
      return
    }
    const form = new FormData()
    form.append('file', evt.target.files[0])
    await axios.post(`/api/trip/${trip.id}/route`, form, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'App-User': `${session.id}`, // helps with trouble-shooting
      },
      onUploadProgress: evt => {
        if (evt.progress) {
          msg.progress(Math.floor(100 * evt.progress))
        }
      },
    })
    msg.succeed()
    api.invalidate(TripResource.get, {id: trip.id})
  }

  return (
    <Button component="label" color="primary" title="Upload Route">
      {children}
      <input id="files" type="file" accept=".gpx,.geojson" onChange={onSelected} hidden />
    </Button>
  )
}
